import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {StorageService} from '../../core/storage/storage.service';
import {BaseHttpService} from '../../core/services/base-http.service';
import {Observable, of} from 'rxjs';
import {DriverModel} from '../models/driver.model';
import {ScoringRankModel} from '../models/scoring-rank.model';
import {RankTypeEnum} from '../models/rank-type.enum';
import {ScoringSummaryModel} from '../models/scoring-summary.model';
import {config} from '../../shared/config';
import {map} from 'rxjs/operators';
import {PeriodStatsModel} from '../models/period-stats.model';
import {VehicleScoringRankModel} from '../models/vehicle-scoring-rank.model';
import {DriverScoringRankModel} from '../models/driver-scoring-rank.model';
import {GroupScoringRankModel} from '../models/group-scoring-rank.model';
import {DateHelper} from '../../shared/helpers/date.helper';
import {VehiclePeriodStatsModel} from '../models/vehicle-period-stats.model';
import {DriverPeriodStatsModel} from '../models/driver-period-stats.model';
import {GroupPeriodStatsModel} from '../models/group-period-stats.model';
import {PeriodTypeEnum} from '../../shared/models/period-type.enum';
import { GroupScoringSummaryResponseModel } from '../models/group-scoring-summary-response.model';
import { VehicleScoringSummaryResponseModel } from '../models/vehicle-scoring-summary-response.model';
import { DriverScoringSummaryResponseModel } from '../models/driver-scoring-summary-response.model';

@Injectable({
  providedIn: 'root'
})
export class ScoringService  extends BaseHttpService {

  constructor(
    protected _http: HttpClient,
    protected _storage: StorageService,
  ) {
    super(_http, _storage);
  }

  getDriverList(): Observable<DriverModel[]> {
    const fields = ['*'];

    // return of([
    //   {driverId: 1, driverName: 'Jan bez ziemi', generalScoring: 0},
    //   {driverId: 2, driverName: 'Bolesław Krzywousty', generalScoring: 0},
    //   {driverId: 3, driverName: 'Jan Bąk', generalScoring: 0},
    // ]);

    return this.get(config.generateApiUrl('scoring/driver/list', fields, null, '2.0')).pipe(
      map((stat) => {
        const st = stat['data'] || {data: { items: []}};
        return st.items as DriverModel[];
      })
    );
  }


  getRankingData(rank: RankTypeEnum, dates: string[]): Observable<ScoringRankModel[]> {
    if (!rank || !dates || dates?.length < 2) { return of(null); }
    if (rank === RankTypeEnum.DRIVERS) {
      return this.getDriversRank(dates[0], dates[1]);
    } else if (rank === RankTypeEnum.VEHICLES) {
      return this.getVehiclesRank(dates[0], dates[1]);
    } else if (rank === RankTypeEnum.GROUPS) {
      return this.getGroupsRank(dates[0], dates[1]);
    }
    return of([]);
  }

  getVehiclesSummary(dateFrom: string, dateTo: string): Observable<ScoringSummaryModel> {
    const fields = ['*'];
    return this.get(config.generateApiUrl(`scoring/vehicles/summary/dateFrom/${dateFrom}/dateTo/${dateTo}`, fields, null, '2.2')).pipe(
      map((res: any) => {
        const data = res?.data as VehicleScoringSummaryResponseModel;
        return {...data, ids: data.unitIds, rankType: RankTypeEnum.VEHICLES} as ScoringSummaryModel;
      })
    );
  }

  getVehiclesPeriodStats(dateFrom: string, dateTo: string): Observable<PeriodStatsModel> {
    const fields = ['*'];
    const period = DateHelper.setPeriodType(new Date(dateTo[0]), new Date(dateTo[1]));
    return this.get(config.generateApiUrl(`scoring/vehicles/stats/dateFrom/${dateFrom}/dateTo/${dateTo}/periodType/${period}`, fields, null, '2.2')).pipe(
      map((res: any) => {
        const data = res?.data as VehiclePeriodStatsModel;
        return {...data, ids: data.unitIds, rankType: RankTypeEnum.VEHICLES} as PeriodStatsModel;
      })
    );
  }

  getVehiclesRank(dateFrom: string, dateTo: string): Observable<ScoringRankModel[]> {
    const fields = ['*'];
    return this.get(config.generateApiUrl(`scoring/vehicles/rank/dateFrom/${dateFrom}/dateTo/${dateTo}`, fields, null, '2.2')).pipe(
      map((res: any) => {
        const data = res?.data ? res?.data as VehicleScoringRankModel[] : [];
        return data?.map((d: VehicleScoringRankModel) => {
          return {...d, id: d.unitId, rankType: RankTypeEnum.VEHICLES } as ScoringRankModel;
        });
      })
    );
  }

  getGroupsRank(dateFrom: string, dateTo: string): Observable<ScoringRankModel[]> {
    const fields = ['*'];
    return this.get(config.generateApiUrl(`scoring/groups/rank/dateFrom/${dateFrom}/dateTo/${dateTo}`, fields, null, '2.2')).pipe(
      map((res: any) => {
        const data =  res?.data ? res?.data['items'] as GroupScoringRankModel[] : [];
        return data?.map((d: GroupScoringRankModel) => {
          return {...d, id: d.unitGroupId, rankType: RankTypeEnum.GROUPS } as ScoringRankModel;
        });
      })
    );
  }

  getDriversRank(dateFrom: string, dateTo: string): Observable<ScoringRankModel[]> {
    const fields = ['*'];
    return this.get(config.generateApiUrl(`scoring/drivers/rank/dateFrom/${dateFrom}/dateTo/${dateTo}`, fields, null, '2.2')).pipe(
      map((res: any) => {
        const data =  res?.data ? res?.data['items'] as DriverScoringRankModel[] : [];
        return data?.map((d: DriverScoringRankModel) => {
          return {...d, id: d.driverId, rankType: RankTypeEnum.DRIVERS } as ScoringRankModel;
        });
      })
    );
  }

  getDriverSummary(dateFrom: string, dateTo: string): Observable<ScoringSummaryModel> {
    const fields = ['*'];
    return this.get(config.generateApiUrl(`scoring/drivers/summary/dateFrom/${dateFrom}/dateTo/${dateTo}`, fields, null, '2.2')).pipe(
      map((res: any) => {
        const data = res?.data?.item as DriverScoringSummaryResponseModel;
        return {...data, ids: data.driverIds, rankType: RankTypeEnum.DRIVERS} as ScoringSummaryModel;
      })
    );
  }

  getDriverPeriodStats(dateFrom: string, dateTo: string): Observable<PeriodStatsModel> {
    const fields = ['*'];
    const period = DateHelper.setPeriodType(new Date(dateTo[0]), new Date(dateTo[1]));
    return this.get(config.generateApiUrl(`scoring/drivers/stats/dateFrom/${dateFrom}/dateTo/${dateTo}/periodType/${period}`, fields, null, '2.2')).pipe(
      map((res: any) => {
        const data = res?.data?.item as DriverPeriodStatsModel;
        return {...data, ids: data.driverIds, rankType: RankTypeEnum.DRIVERS} as PeriodStatsModel;
      })
    );
  }

  getDriverSummaryById(dateFrom: string, dateTo: string, driverId: number): Observable<ScoringSummaryModel> {
    const fields = ['*'];
    return this.get(config.generateApiUrl(`scoring/drivers/${driverId}/summary/dateFrom/${dateFrom}/dateTo/${dateTo}`, fields, null, '2.2')).pipe(
      map((res: any) => res?.data?.item as ScoringSummaryModel)
    );
  }

  getDriverPeriodStatsById(dateFrom: string, dateTo: string, driverId: number, periodType?: PeriodTypeEnum): Observable<PeriodStatsModel> {
    const fields = ['*'];
    const period = periodType ? periodType : DateHelper.setPeriodType(new Date(dateTo[0]), new Date(dateTo[1]));
    return this.get(config.generateApiUrl(`scoring/drivers/${driverId}/stats/dateFrom/${dateFrom}/dateTo/${dateTo}/periodType/${period}`, fields, null, '2.2')).pipe(
      map((res: any) => {
        const data = res?.data?.item as DriverPeriodStatsModel;
        return {...data, ids: data.driverIds, rankType: RankTypeEnum.DRIVERS} as PeriodStatsModel;
      })
    );
  }

  getGroupsSummary(dateFrom: string, dateTo: string): Observable<ScoringSummaryModel> {
    const fields = ['*'];
    return this.get(config.generateApiUrl(`scoring/groups/summary/dateFrom/${dateFrom}/dateTo/${dateTo}`, fields, null, '2.2')).pipe(
      map((res: any) => {
        const data = res?.data?.item as GroupScoringSummaryResponseModel;
        return {...data, ids: data.unitGroupIds, rankType: RankTypeEnum.GROUPS} as ScoringSummaryModel
      })
    );
  }

  getGroupsPeriodStats(dateFrom: string, dateTo: string): Observable<PeriodStatsModel> {
    const fields = ['*'];
    const period = DateHelper.setPeriodType(new Date(dateTo[0]), new Date(dateTo[1]));
    return this.get(config.generateApiUrl(`scoring/groups/stats/dateFrom/${dateFrom}/dateTo/${dateTo}/periodType/${period}`, fields, null, '2.2')).pipe(
      map((res: any) => {
        const data = res?.data?.item as GroupPeriodStatsModel;
        return {...data, ids: data.unitGroupIds, rankType: RankTypeEnum.GROUPS} as PeriodStatsModel;
      })
    );
  }
}
